import React, { FC, ReactNode, useEffect, useState } from "react";
import { useSelector } from 'react-redux';
import { useHistory } from "react-router-dom";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import { useDialog } from "../../../components/dialog/DialogContext";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import { CUSTOM_VALUES, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../../../constants/command-status";
import Commands from '../../../constants/commands';
import { MonitoringSubCmds } from '../../../constants/sub-commands';
import TransactionsViewerTab from "../../../containers/transaction-viewer-tab";
import { useLocale } from "../../../contexts/localeContext";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import { Button } from "../../../components/button/Button";
import { useIcaWebsocketContext } from "../../../contexts/websocket/IcaWebsocketContext";
import PauseIcon from '../../../images/PauseIcon.svg';
import PlayIcon from '../../../images/PlayIcon.svg';
import { Category } from "../../../models/Category";
import { ExecuteCommand } from "../../../models/ExecuteCommand";
import { executeCommandTest } from "../../../services/executeCommandService";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { IAppState } from '../../../store/types';

const Transaction: FC = () => {
    const { translate } = useLocale();
    const [selected, setSelected] = useState(0);
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([{
        displayName: translate.byKey(TranslationKeys.transactions),
        heading: translate.byKey(TranslationKeys.transactions),
        value: "",
        id: 0,
        count: 0,
    }]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const [broadcastTransaction, setBroadcastTransaction] = useState(false);
    const user = useSelector((state: IAppState) => (state.userInfo));
    const icaWebSocket = useIcaWebsocketContext();
    const dialog = useDialog();

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent = [
                <TransactionsViewerTab
                    categories={categories}
                    setCategories={setCategories} />,
            ];
            setTabContent(newTabContent);
        }
    }, [categories]);

    const onBroadcastChange = () => {
        //simulateTransaction(); // Uncomment to test with fake transactions.
        setBroadcastTransaction(!broadcastTransaction);

        if (!broadcastTransaction) { // Stop monitoring
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_STOP);
        }
        else { // Start monitoring again
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_TRANSACTIONS, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START, CUSTOM_VALUES.NO_REFRESH);
        }
    }

    /**
     * For testing purposes only!
     * Requires a pre-populated database that already has some transactions.
     * We use the fixed address to lookup the device and the event name to lookup the event.
     * Then find an existing transaction in the database and send it out on websockets again.
     * Allows us to demo transactions without access to the hardware.
     * E.g. call simulateTransaction(); from onBroadcastChange to test using the Pause button.
     */
    const simulateTransaction = () => {
        const testData = {
            testData: [
                "6E01DE70=EV_Full_Offline_Upload_Begin",
                "6E01DE70=EV_Host_Timeout",
                "6E01DE70=EV_Unit_Power_up",
                "0058BCFD=EV_Anti_Tamper",
                "0057A510=EV_RTE_2",
                "0057A50F=EV_Denied_Unknown_Tag_In",
                "6E01DE70=EV_Full_Offline_Upload_End",
                "0057A50F=EV_Allowed_Normal_In",
                "0057A510=EV_Allowed_Normal_Out"
            ]
        }

        const command: ExecuteCommand = {
            cmd: Commands.Device.label,
            cmdSubType: MonitoringSubCmds.Test_Resend_Transactions,
            userLoginId: user.id,
            jsonResponse: "",
        }
        executeCommandTest(command, testData)
            .then(() => {
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
            })
    }

    return <ActivityFrame busy={busy} className="flex flex-grow overflow-y-hidden">
        <div id="transactions" className="pt-4 pl-4 flex flex-col flex-grow overflow-y-hidden">
            <Breadcrumb
                path={[
                    {
                        value: "/access-control/monitoring",
                        displayName: translate.byKey(TranslationKeys.monitoring),
                    },
                    {
                        value: "/access-control/monitoring/transactions",
                        displayName: translate.byKey(TranslationKeys.transactions),
                    }
                ]}
                onClick={(item) => history.push(item.value)} />
            <Tabs
                className="bg-white overflow-y-hidden"
                tabs={getTabsListFromLabels(categories)}
                onSelect={(i) => setSelected(i)}
                selected={selected}
                onAdd={() => { }}
                add={false}
                tabStyle={{}}>
                {tabContent[selected]}
                <div className="mb-viewport" />
            </Tabs>
            <div className="flex flex-row justify-end items-center flex-shrink-0 h-16 pr-8 border-t border-gray-200 bg-white">
                <Button
                    variant="submit"
                    shape="rounded"
                    active={broadcastTransaction}
                    onClick={onBroadcastChange}
                    id="addDeviceTimePatternbutton">
                    <img
                        src={broadcastTransaction ? PlayIcon : PauseIcon}
                        className={`px-2 ${broadcastTransaction ? '' : 'w-6 py-1'}`}
                        alt="" />
                    <span className="mr-2">{broadcastTransaction ? translate.byKey(TranslationKeys.play) : translate.byKey(TranslationKeys.pause)}</span>
                </Button>
            </div>
        </div>
    </ActivityFrame>
};

export default Transaction;
